import type {
  AvailabilityDateOverride,
  AvailabilityFormValues,
  AvailabilityTimeRange,
} from "@/lib/cal/availability-mapper";
import { date, time } from "@/lib/time";

const MINUTES_PER_DAY = 24 * 60;
const LOCAL_DATE_FORMAT = "YYYY-MM-DD";
const LOCAL_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm";

interface TimeInterval {
  startMinutes: number;
  endMinutes: number;
}

interface NormalizedDayRange {
  dayIndex: number;
  startMinutes: number;
  endMinutes: number;
}

interface NormalizedDateRange {
  date: string;
  startMinutes: number;
  endMinutes: number;
}

interface IntersectAvailabilitySchedulesInput {
  appointmentAvailability: AvailabilityFormValues;
  hostAvailability: AvailabilityFormValues;
  targetTimeZone: string;
  referenceDate?: string;
  name?: string;
  isDefault?: boolean;
}

interface NormalizeAvailabilityToTimeZoneInput {
  availability: AvailabilityFormValues;
  targetTimeZone: string;
  referenceDate?: string;
}

interface BuildAvailabilityRangesByDateInput extends NormalizeAvailabilityToTimeZoneInput {
  dates: string[];
}

/**
 * Converts one weekly availability schedule into a target timezone, splitting ranges across day boundaries.
 */
function normalizeScheduleToTimeZone({
  schedule,
  sourceTimeZone,
  targetTimeZone,
  referenceDate,
}: {
  schedule: AvailabilityTimeRange[][];
  sourceTimeZone: string;
  targetTimeZone: string;
  referenceDate: string;
}): AvailabilityTimeRange[][] {
  if (sourceTimeZone === targetTimeZone) {
    return mergeScheduleRanges({
      schedule: schedule.map((ranges) => [...ranges]),
    });
  }

  const referenceWeekStart = resolveReferenceWeekStart({
    referenceDate,
    targetTimeZone,
  });
  const normalizedRanges = schedule.flatMap((ranges, dayIndex) => {
    const occurrenceDate = referenceWeekStart.add(dayIndex, "day").format("YYYY-MM-DD");

    return ranges.flatMap((range) => {
      const start = time.tz(
        `${occurrenceDate} ${range.start}`,
        LOCAL_DATE_TIME_FORMAT,
        sourceTimeZone,
      );
      const end = time.tz(`${occurrenceDate} ${range.end}`, LOCAL_DATE_TIME_FORMAT, sourceTimeZone);
      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
        return [];
      }

      return splitRangeAcrossTargetDays({
        start: start.tz(targetTimeZone),
        end: end.tz(targetTimeZone),
      });
    });
  });

  const nextSchedule = Array.from({ length: 7 }, () => [] as AvailabilityTimeRange[]);
  normalizedRanges.forEach((range) => {
    nextSchedule[range.dayIndex]?.push({
      start: formatMinutes(range.startMinutes),
      end: formatMinutes(range.endMinutes),
    });
  });

  return mergeScheduleRanges({ schedule: nextSchedule });
}

/**
 * Uses the chosen week anchor so timezone conversion follows the real DST offset for that week.
 */
function resolveReferenceWeekStart({
  referenceDate,
  targetTimeZone,
}: {
  referenceDate: string;
  targetTimeZone: string;
}): ReturnType<typeof time> {
  const isLocalDateReference = /^\d{4}-\d{2}-\d{2}$/.test(referenceDate);
  const localizedReferenceDate = isLocalDateReference
    ? time.tz(referenceDate, LOCAL_DATE_FORMAT, targetTimeZone)
    : date(referenceDate).tz(targetTimeZone);
  const validReferenceDate = localizedReferenceDate.isValid()
    ? localizedReferenceDate.startOf("day")
    : time().tz(targetTimeZone).startOf("day");

  return validReferenceDate.subtract(validReferenceDate.day(), "day");
}

/**
 * Splits a timezone-converted interval into one or more day-scoped ranges.
 */
function splitRangeAcrossTargetDays({
  start,
  end,
}: {
  start: ReturnType<typeof time>;
  end: ReturnType<typeof time>;
}): NormalizedDayRange[] {
  const normalizedRanges: NormalizedDayRange[] = [];
  let cursor = start;

  while (cursor.valueOf() < end.valueOf()) {
    const dayStart = cursor.startOf("day");
    const nextDayStart = dayStart.add(1, "day");
    const segmentEnd = end.valueOf() < nextDayStart.valueOf() ? end : nextDayStart;
    const dayIndex = dayStart.day();
    const startMinutes = cursor.hour() * 60 + cursor.minute();
    const endMinutes =
      segmentEnd.valueOf() === nextDayStart.valueOf()
        ? MINUTES_PER_DAY
        : segmentEnd.hour() * 60 + segmentEnd.minute();

    if (endMinutes > startMinutes) {
      normalizedRanges.push({
        dayIndex,
        startMinutes,
        endMinutes,
      });
    }

    cursor = segmentEnd;
  }

  return normalizedRanges;
}

/**
 * Merges overlapping or touching ranges within each day.
 */
function mergeScheduleRanges({
  schedule,
}: {
  schedule: AvailabilityTimeRange[][];
}): AvailabilityTimeRange[][] {
  return schedule.map((ranges) => {
    const intervals = ranges
      .map((range) => toTimeInterval({ range }))
      .filter((interval): interval is TimeInterval => interval !== undefined)
      .sort((left, right) => left.startMinutes - right.startMinutes);

    if (intervals.length === 0) {
      return [];
    }

    const mergedIntervals: TimeInterval[] = [intervals[0]];
    intervals.slice(1).forEach((interval) => {
      const currentInterval = mergedIntervals[mergedIntervals.length - 1];
      if (interval.startMinutes <= currentInterval.endMinutes) {
        currentInterval.endMinutes = Math.max(currentInterval.endMinutes, interval.endMinutes);
        return;
      }

      mergedIntervals.push({ ...interval });
    });

    return mergedIntervals.map((interval) => ({
      start: formatMinutes(interval.startMinutes),
      end: formatMinutes(interval.endMinutes),
    }));
  });
}

/**
 * Intersects appointment availability with exactly one host schedule.
 */
export function intersectAvailabilitySchedules({
  appointmentAvailability,
  hostAvailability,
  targetTimeZone,
  referenceDate,
  name,
  isDefault = false,
}: IntersectAvailabilitySchedulesInput): AvailabilityFormValues {
  const effectiveReferenceDate = referenceDate ?? time().toISOString();
  const normalizedHostSchedule = normalizeScheduleToTimeZone({
    schedule: hostAvailability.schedule,
    sourceTimeZone: hostAvailability.timeZone,
    targetTimeZone,
    referenceDate: effectiveReferenceDate,
  });
  const normalizedHostDateOverrides = normalizeDateOverridesToTimeZone({
    availability: hostAvailability,
    normalizedWeeklySchedule: normalizedHostSchedule,
    targetTimeZone,
  });

  if (appointmentAvailability.inheritHostAvailability) {
    return {
      name: name ?? appointmentAvailability.name,
      timeZone: targetTimeZone,
      schedule: normalizedHostSchedule,
      dateOverrides: normalizedHostDateOverrides,
      isDefault,
      inheritHostAvailability: false,
    };
  }

  const normalizedAppointmentSchedule = normalizeScheduleToTimeZone({
    schedule: appointmentAvailability.schedule,
    sourceTimeZone: appointmentAvailability.timeZone,
    targetTimeZone,
    referenceDate: effectiveReferenceDate,
  });
  const normalizedAppointmentDateOverrides = normalizeDateOverridesToTimeZone({
    availability: appointmentAvailability,
    normalizedWeeklySchedule: normalizedAppointmentSchedule,
    targetTimeZone,
  });

  const intersectedSchedule = normalizedAppointmentSchedule.map((appointmentRanges, dayIndex) => {
    const hostRanges = normalizedHostSchedule[dayIndex] ?? [];
    return intersectDayRanges({
      leftRanges: appointmentRanges,
      rightRanges: hostRanges,
    });
  });
  const intersectedDateOverrides = intersectDateOverrides({
    appointmentDateOverrides: normalizedAppointmentDateOverrides,
    hostDateOverrides: normalizedHostDateOverrides,
    normalizedAppointmentSchedule,
    normalizedHostSchedule,
    intersectedWeeklySchedule: intersectedSchedule,
    targetTimeZone,
  });

  return {
    name: name ?? appointmentAvailability.name,
    timeZone: targetTimeZone,
    schedule: intersectedSchedule,
    dateOverrides: intersectedDateOverrides,
    isDefault,
  };
}

/**
 * Returns whether the schedule contains at least one bookable range.
 */
export function doesAvailabilityHaveAnyRanges({
  availability,
}: {
  availability: AvailabilityFormValues;
}): boolean {
  return (
    availability.schedule.some((dayRanges) => dayRanges.length > 0) ||
    availability.dateOverrides.some((dateOverride) => dateOverride.ranges.length > 0)
  );
}

/**
 * Converts one availability schedule into the calendar's display timezone.
 */
export function normalizeAvailabilityToTimeZone({
  availability,
  targetTimeZone,
  referenceDate,
}: NormalizeAvailabilityToTimeZoneInput): AvailabilityFormValues {
  const effectiveReferenceDate = referenceDate ?? time().toISOString();
  const normalizedSchedule = normalizeScheduleToTimeZone({
    schedule: availability.schedule,
    sourceTimeZone: availability.timeZone,
    targetTimeZone,
    referenceDate: effectiveReferenceDate,
  });
  const normalizedDateOverrides = normalizeDateOverridesToTimeZone({
    availability,
    normalizedWeeklySchedule: normalizedSchedule,
    targetTimeZone,
  });

  return {
    ...availability,
    timeZone: targetTimeZone,
    schedule: normalizedSchedule,
    dateOverrides: normalizedDateOverrides,
  };
}

/**
 * Resolves the effective availability ranges for each rendered calendar date.
 */
export function buildAvailabilityRangesByDate({
  availability,
  targetTimeZone,
  referenceDate,
  dates,
}: BuildAvailabilityRangesByDateInput): Record<string, AvailabilityTimeRange[]> {
  const normalizedAvailability = normalizeAvailabilityToTimeZone({
    availability,
    targetTimeZone,
    referenceDate,
  });
  const normalizedOverridesByDate = buildDateOverrideMap({
    dateOverrides: normalizedAvailability.dateOverrides,
  });

  return Object.fromEntries(
    Array.from(new Set(dates)).map((dateValue) => {
      const targetDay = time.tz(dateValue, LOCAL_DATE_FORMAT, targetTimeZone);
      if (!targetDay.isValid()) {
        return [dateValue, []];
      }

      return [
        dateValue,
        normalizedOverridesByDate.get(dateValue) ??
          normalizedAvailability.schedule[targetDay.day()] ??
          [],
      ];
    }),
  );
}

/**
 * Normalizes date-specific availability so explicit overrides survive timezone conversion.
 */
function normalizeDateOverridesToTimeZone({
  availability,
  normalizedWeeklySchedule,
  targetTimeZone,
}: {
  availability: AvailabilityFormValues;
  normalizedWeeklySchedule: AvailabilityTimeRange[][];
  targetTimeZone: string;
}): AvailabilityDateOverride[] {
  const sourceOverridesByDate = buildDateOverrideMap({
    dateOverrides: availability.dateOverrides,
  });
  if (sourceOverridesByDate.size === 0) {
    return [];
  }

  const sourceDatesToEvaluate = Array.from(sourceOverridesByDate.keys())
    .flatMap((overrideDate) => {
      const sourceDate = time
        .tz(overrideDate, LOCAL_DATE_FORMAT, availability.timeZone)
        .startOf("day");
      if (!sourceDate.isValid()) {
        return [];
      }

      return [-1, 0, 1].map((offset) => sourceDate.add(offset, "day").format(LOCAL_DATE_FORMAT));
    })
    .filter((dateValue, index, dates) => dates.indexOf(dateValue) === index)
    .sort((left, right) => left.localeCompare(right));

  const baselineRangesByTargetDate = new Map<string, AvailabilityTimeRange[]>();
  const actualRangesByTargetDate = new Map<string, AvailabilityTimeRange[]>();

  sourceDatesToEvaluate.forEach((sourceDate) => {
    const sourceDay = time.tz(sourceDate, LOCAL_DATE_FORMAT, availability.timeZone);
    if (!sourceDay.isValid()) {
      return;
    }

    const baselineRanges = availability.schedule[sourceDay.day()] ?? [];
    const actualRanges = sourceOverridesByDate.has(sourceDate)
      ? (sourceOverridesByDate.get(sourceDate) ?? [])
      : baselineRanges;

    appendConvertedDateRanges({
      dateValue: sourceDate,
      ranges: baselineRanges,
      sourceTimeZone: availability.timeZone,
      targetTimeZone,
      rangesByTargetDate: baselineRangesByTargetDate,
    });
    appendConvertedDateRanges({
      dateValue: sourceDate,
      ranges: actualRanges,
      sourceTimeZone: availability.timeZone,
      targetTimeZone,
      rangesByTargetDate: actualRangesByTargetDate,
    });
  });

  const affectedTargetDates = Array.from(
    new Set([...baselineRangesByTargetDate.keys(), ...actualRangesByTargetDate.keys()]),
  ).sort((left, right) => left.localeCompare(right));

  return affectedTargetDates.flatMap((targetDate) => {
    const targetDay = time.tz(targetDate, LOCAL_DATE_FORMAT, targetTimeZone);
    if (!targetDay.isValid()) {
      return [];
    }

    const defaultRanges = normalizedWeeklySchedule[targetDay.day()] ?? [];
    const actualRanges = mergeDayRanges({
      ranges: actualRangesByTargetDate.get(targetDate) ?? [],
    });
    if (doDayRangesMatch({ leftRanges: actualRanges, rightRanges: defaultRanges })) {
      return [];
    }

    return [
      {
        date: targetDate,
        ranges: actualRanges,
      },
    ];
  });
}

/**
 * Intersects the specific dates where either side overrides the default weekly schedule.
 */
function intersectDateOverrides({
  appointmentDateOverrides,
  hostDateOverrides,
  normalizedAppointmentSchedule,
  normalizedHostSchedule,
  intersectedWeeklySchedule,
  targetTimeZone,
}: {
  appointmentDateOverrides: AvailabilityDateOverride[];
  hostDateOverrides: AvailabilityDateOverride[];
  normalizedAppointmentSchedule: AvailabilityTimeRange[][];
  normalizedHostSchedule: AvailabilityTimeRange[][];
  intersectedWeeklySchedule: AvailabilityTimeRange[][];
  targetTimeZone: string;
}): AvailabilityDateOverride[] {
  const appointmentOverridesByDate = buildDateOverrideMap({
    dateOverrides: appointmentDateOverrides,
  });
  const hostOverridesByDate = buildDateOverrideMap({
    dateOverrides: hostDateOverrides,
  });
  const overrideDates = Array.from(
    new Set([...appointmentOverridesByDate.keys(), ...hostOverridesByDate.keys()]),
  ).sort((left, right) => left.localeCompare(right));

  return overrideDates.flatMap((overrideDate) => {
    const targetDay = time.tz(overrideDate, LOCAL_DATE_FORMAT, targetTimeZone);
    if (!targetDay.isValid()) {
      return [];
    }

    const dayIndex = targetDay.day();
    const appointmentRanges =
      appointmentOverridesByDate.get(overrideDate) ?? normalizedAppointmentSchedule[dayIndex] ?? [];
    const hostRanges =
      hostOverridesByDate.get(overrideDate) ?? normalizedHostSchedule[dayIndex] ?? [];
    const intersectedRanges = mergeDayRanges({
      ranges: intersectDayRanges({
        leftRanges: appointmentRanges,
        rightRanges: hostRanges,
      }),
    });
    const defaultRanges = intersectedWeeklySchedule[dayIndex] ?? [];
    if (doDayRangesMatch({ leftRanges: intersectedRanges, rightRanges: defaultRanges })) {
      return [];
    }

    return [
      {
        date: overrideDate,
        ranges: intersectedRanges,
      },
    ];
  });
}

/**
 * Intersects two day-range lists.
 */
function intersectDayRanges({
  leftRanges,
  rightRanges,
}: {
  leftRanges: AvailabilityTimeRange[];
  rightRanges: AvailabilityTimeRange[];
}): AvailabilityTimeRange[] {
  const leftIntervals = leftRanges
    .map((range) => toTimeInterval({ range }))
    .filter((interval): interval is TimeInterval => interval !== undefined);
  const rightIntervals = rightRanges
    .map((range) => toTimeInterval({ range }))
    .filter((interval): interval is TimeInterval => interval !== undefined);

  const intersectedRanges: AvailabilityTimeRange[] = [];
  let leftIndex = 0;
  let rightIndex = 0;

  while (leftIndex < leftIntervals.length && rightIndex < rightIntervals.length) {
    const leftInterval = leftIntervals[leftIndex];
    const rightInterval = rightIntervals[rightIndex];
    const overlapStart = Math.max(leftInterval.startMinutes, rightInterval.startMinutes);
    const overlapEnd = Math.min(leftInterval.endMinutes, rightInterval.endMinutes);

    if (overlapEnd > overlapStart) {
      intersectedRanges.push({
        start: formatMinutes(overlapStart),
        end: formatMinutes(overlapEnd),
      });
    }

    if (leftInterval.endMinutes <= rightInterval.endMinutes) {
      leftIndex += 1;
    } else {
      rightIndex += 1;
    }
  }

  return intersectedRanges;
}

function buildDateOverrideMap({
  dateOverrides,
}: {
  dateOverrides: AvailabilityDateOverride[];
}): Map<string, AvailabilityTimeRange[]> {
  return dateOverrides.reduce((rangesByDate, dateOverride) => {
    if (!dateOverride?.date || !Array.isArray(dateOverride.ranges)) {
      return rangesByDate;
    }

    rangesByDate.set(dateOverride.date, mergeDayRanges({ ranges: dateOverride.ranges }));
    return rangesByDate;
  }, new Map<string, AvailabilityTimeRange[]>());
}

function appendConvertedDateRanges({
  dateValue,
  ranges,
  sourceTimeZone,
  targetTimeZone,
  rangesByTargetDate,
}: {
  dateValue: string;
  ranges: AvailabilityTimeRange[];
  sourceTimeZone: string;
  targetTimeZone: string;
  rangesByTargetDate: Map<string, AvailabilityTimeRange[]>;
}): void {
  ranges.forEach((range) => {
    const start = time.tz(`${dateValue} ${range.start}`, LOCAL_DATE_TIME_FORMAT, sourceTimeZone);
    const end = time.tz(`${dateValue} ${range.end}`, LOCAL_DATE_TIME_FORMAT, sourceTimeZone);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
      return;
    }

    splitRangeAcrossTargetDates({
      start: start.tz(targetTimeZone),
      end: end.tz(targetTimeZone),
    }).forEach((dateRange) => {
      const existingRanges = rangesByTargetDate.get(dateRange.date) ?? [];
      existingRanges.push({
        start: formatMinutes(dateRange.startMinutes),
        end: formatMinutes(dateRange.endMinutes),
      });
      rangesByTargetDate.set(dateRange.date, existingRanges);
    });
  });
}

function splitRangeAcrossTargetDates({
  start,
  end,
}: {
  start: ReturnType<typeof time>;
  end: ReturnType<typeof time>;
}): NormalizedDateRange[] {
  const normalizedRanges: NormalizedDateRange[] = [];
  let cursor = start;

  while (cursor.valueOf() < end.valueOf()) {
    const dayStart = cursor.startOf("day");
    const nextDayStart = dayStart.add(1, "day");
    const segmentEnd = end.valueOf() < nextDayStart.valueOf() ? end : nextDayStart;
    const startMinutes = cursor.hour() * 60 + cursor.minute();
    const endMinutes =
      segmentEnd.valueOf() === nextDayStart.valueOf()
        ? MINUTES_PER_DAY
        : segmentEnd.hour() * 60 + segmentEnd.minute();

    if (endMinutes > startMinutes) {
      normalizedRanges.push({
        date: dayStart.format(LOCAL_DATE_FORMAT),
        startMinutes,
        endMinutes,
      });
    }

    cursor = segmentEnd;
  }

  return normalizedRanges;
}

function mergeDayRanges({ ranges }: { ranges: AvailabilityTimeRange[] }): AvailabilityTimeRange[] {
  return mergeScheduleRanges({ schedule: [ranges] })[0] ?? [];
}

function doDayRangesMatch({
  leftRanges,
  rightRanges,
}: {
  leftRanges: AvailabilityTimeRange[];
  rightRanges: AvailabilityTimeRange[];
}): boolean {
  const normalizedLeftRanges = mergeDayRanges({ ranges: leftRanges });
  const normalizedRightRanges = mergeDayRanges({ ranges: rightRanges });
  if (normalizedLeftRanges.length !== normalizedRightRanges.length) {
    return false;
  }

  return normalizedLeftRanges.every((range, index) => {
    const rightRange = normalizedRightRanges[index];
    return range.start === rightRange?.start && range.end === rightRange.end;
  });
}

/**
 * Converts a schedule range into minute offsets.
 */
function toTimeInterval({ range }: { range: AvailabilityTimeRange }): TimeInterval | undefined {
  const [startHour, startMinute] = range.start
    .split(":")
    .map((value) => Number.parseInt(value, 10));
  const [endHour, endMinute] = range.end.split(":").map((value) => Number.parseInt(value, 10));
  if (
    [startHour, startMinute, endHour, endMinute].some((value) => Number.isNaN(value)) ||
    endHour * 60 + endMinute <= startHour * 60 + startMinute
  ) {
    return undefined;
  }

  return {
    startMinutes: startHour * 60 + startMinute,
    endMinutes: endHour * 60 + endMinute,
  };
}

/**
 * Formats a minute offset as HH:mm.
 *
 * Cal.com's schedule API rejects anything outside 00:00-23:59 (it previously
 * accepted 24:00 as "end of day", but the v2 validator enforces the HH:MM
 * range strictly). When a range lands exactly on a midnight boundary — which
 * happens whenever a schedule has to be split across target-TZ days — we clamp
 * the end of that range to 23:59 instead of emitting the invalid "24:00".
 * This costs at most one minute of availability per crossed midnight; that's
 * well below any bookable slot length.
 */
function formatMinutes(totalMinutes: number): string {
  if (totalMinutes > 0 && totalMinutes % MINUTES_PER_DAY === 0) {
    return "23:59";
  }

  const normalizedMinutes = ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;

  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}
